import { OpenPanelComponent } from "@openpanel/nextjs";
// import * as Sentry from "@sentry/nextjs";
import { Hydrate, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import ThemeProvider from "../components/ThemeProvider";
import { AppWrapper } from "../contexts/StoreDataContext";
import { getStorefrontSession } from "../services/frontend/storefront-apis";
// import { nodeEnv } from "../services/helper";
import { registerSuperProperties, trackPageView } from "../utils/analytics";
import analyticsConfig from "../utils/analyticsConfig";
import { installApiTracing } from "../utils/apiTrace";
import { installClientLogCapture } from "../utils/clientLogs";
import { trackMetaPageView } from "../utils/metaPixel";
import { initPostHog } from "../utils/posthogClient";
import { buildStoreSuperProperties } from "../utils/storeSuperProperties";
import { clearLastTraceId } from "../utils/trace";
import { initializeTracking } from "../utils/tracking";
import "../styles/globals.css";

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const [themeData, setThemeData] = useState(null);
  const [storeData, setStoreData] = useState(null);
  const [error, setError] = useState(null);
  const [storeUrl, setStoreUrl] = useState(null);
  const pixelId = storeData?.metaPixel;

  // The initial $pageview must not ship before registerSuperProperties has
  // set store context below, or the very first pageview of every session is
  // missing it. Fired once, at whichever of "PostHog started" / "session
  // fetch settled" happens last.
  const posthogReadyRef = useRef(false);
  const storeFetchSettledRef = useRef(false);
  const initialPageviewFiredRef = useRef(false);
  const maybeFireInitialPageview = () => {
    if (initialPageviewFiredRef.current) return;
    if (!posthogReadyRef.current || !storeFetchSettledRef.current) return;
    initialPageviewFiredRef.current = true;
    trackPageView(window.location.pathname);
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            retry: 1,
            // Chrome can report navigator.onLine === false while the network is fine; the
            // default networkMode would then pause every query and leave the store on
            // skeletons forever. We have no offline UX, so always attempt the request.
            networkMode: "always",
          },
        },
      }),
  );

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const session = await getStorefrontSession();
        const store = session.store;

        // Registered here, synchronously with the fetch that resolves it,
        // rather than in a separate effect keyed on storeData: that effect
        // runs after this component's children have already committed their
        // own mount effects (e.g. STORE_VIEWED), so those events would ship
        // without store context.
        registerSuperProperties(buildStoreSuperProperties(store));

        setStoreData(store);
        setThemeData(session.theme ?? null);
        setError(null);
        setStoreUrl(typeof window !== "undefined" ? window.location.origin : null);
      } catch (err) {
        console.error("Storefront session failed:", err);

        if (err?.code === "STORE_NOT_FOUND" || err?.status === 404) {
          setError(404);
        } else {
          setError({ code: 500 });
        }
      } finally {
        storeFetchSettledRef.current = true;
        maybeFireInitialPageview();
      }
    };

    fetchSession();
  }, []);

  // useEffect(() => {
  //   // Initialize Sentry only on the client side
  //   if (typeof window !== "undefined") {
  //     Sentry.init({
  //       dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || process.env.SENTRY_DSN,
  //       tracesSampleRate: 0,
  //       environment: nodeEnv,
  //       enabled: nodeEnv !== "development",
  //       debug: false,
  //     });
  //   }
  // }, []); // Empty dependency array means this runs once on mount

  useEffect(() => {
    if (typeof window !== "undefined") {
      (async () => {
        const { BrowserAgent } = await import("@newrelic/browser-agent/loaders/browser-agent");
        const options = {
          info: {
            applicationID: 653444045,
            beacon: "bam.nr-data.net",
            errorBeacon: "bam.nr-data.net",
            licenseKey: "NRJS-afa44d47157ac6eb0b9",
            sa: 1,
          },
          init: {
            ajax: {
              deny_list: ["bam.nr-data.net"],
            },
            browser_consent_mode: {
              enabled: false,
            },
            distributed_tracing: {
              enabled: true,
            },
            // Soft-nav SPA harvest was throwing `c.replace is not a function` in
            // bel-serializer (then flooding Sentry). Keep page/ajax metrics; drop SPA.
            soft_navigations: {
              enabled: false,
            },
            performance: {
              capture_detail: false,
              capture_marks: false,
              capture_measures: true,
            },
            privacy: {
              cookies_enabled: true,
            },
          },
          loader_config: {
            accountID: 8478586,
            agentID: 653444045,
            applicationID: 653444045,
            licenseKey: "NRJS-afa44d47157ac6eb0b9",
            trustKey: 8478586,
          },
        };
        try {
          new BrowserAgent(options);
        } catch (err) {
          console.error("New Relic BrowserAgent init failed:", err);
        }
      })();
    }
  }, []);

  // PostHog: start the SDK, forward console warnings/errors, and record the
  // first page view. Everything here no-ops until NEXT_PUBLIC_POSTHOG_KEY is set.
  useEffect(() => {
    if (!initPostHog()) return;
    installClientLogCapture();
    // Behind the same guard on purpose: with no PostHog there is nothing to
    // correlate a trace id *to*, so window.fetch is left entirely unwrapped
    // rather than carrying a patch that can only cost something.
    installApiTracing();
    posthogReadyRef.current = true;
    maybeFireInitialPageview();
  }, []);

  // Page views for client-side route changes. The SDK cannot see these itself
  // because the pages router swaps components without a document load.
  useEffect(() => {
    const handleRouteChange = (url) => {
      // The previous page's trace must not survive the navigation, or an
      // error on the new page gets correlated to a stale trace.
      clearLastTraceId();
      trackPageView(url);
      if (pixelId) trackMetaPageView(pixelId);
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events, pixelId]);

  useEffect(() => {
    if (pixelId) trackMetaPageView(pixelId);
  }, [pixelId]);

  // Initialize tracking on app load
  useEffect(() => {
    // Don't re-capture tracking data on checkout page to preserve UTM parameters
    if (typeof window !== "undefined" && window.location.pathname !== "/checkout") {
      initializeTracking();
    }
  }, []);

  // Determine if OpenPanel should track page views automatically
  const shouldOpenPanelTrackPageViews = analyticsConfig.pageViewProviders.includes("openpanel");

  // GTM remains off. Meta Pixel runs only for stores with a configured Pixel ID.

  return (
    <AppWrapper sharedState={{ storeData, themeData, error, storeUrl }}>
      <Head>
        {/* preconnect hints */}
        {process.env.NEXT_PUBLIC_CDN_HOST ? (
          <link
            rel="preconnect"
            href={`https://${process.env.NEXT_PUBLIC_CDN_HOST.replace(/^https?:\/\//, "")}`}
          />
        ) : null}
      </Head>

      {
        /* OpenPanel initialization */
        analyticsConfig.defaultProviders.includes("openpanel") && (
          <OpenPanelComponent
            clientId={process.env.NEXT_PUBLIC_OPENPANEL_CLIENT_ID}
            trackScreenViews={shouldOpenPanelTrackPageViews}
            trackOutgoingLinks={true}
            profileId={pageProps?.userData?.id}
            apiUrl={process.env.NEXT_PUBLIC_OPENPANEL_API_URL}
            waitForProfile={false}
          />
        )
      }
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <ThemeProvider themeData={themeData}>
            <Component {...pageProps} />
          </ThemeProvider>
        </Hydrate>
      </QueryClientProvider>
    </AppWrapper>
  );
}

export default MyApp;
